import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileText, RefreshCw, Trash2, Upload } from 'lucide-react';
import {
  canProject,
  TRAINING_SOURCE_MAX_FILES,
  type QuestionnaireAnswer,
  type TrainingState,
} from '@allemnii-pulse/shared';
import { useProject } from '../projects/ProjectProvider.js';
import { api, ApiError } from '../../lib/api.js';
import { relativeTime } from '../../lib/time.js';
import {
  SOURCE_FILE_ACCEPT,
  uploadErrorMessage,
  uploadSourceFile,
} from '../../lib/sourceUploads.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceDescription,
  QuestionnaireChoices,
  QuestionnaireDescription,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSubmit,
  QuestionnaireTitle,
} from '../../components/ui/Questionnaire.js';
import {
  postJob,
  useJobRunner,
  useTrainingSessions,
  useTrainingSources,
  useTrainingState,
} from './useTraining.js';

/** The answer the user gave to `questionId`, as one line. */
function answerText(state: TrainingState, questionId: string): string {
  const a = state.answers.find((x) => x.questionId === questionId);
  return [a?.selectedOptionLabel, a?.freeText].filter(Boolean).join(' — ') || '—';
}

function ProfileCard({
  projectId,
  state,
  canManage,
}: {
  projectId: string;
  state: TrainingState;
  canManage: boolean;
}) {
  const [form, setForm] = useState({
    audience: '',
    language: '',
    minSessions: '',
    maxSessions: '',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dirty) return;
    setForm({
      audience: state.audience ?? '',
      language: state.language ?? '',
      minSessions: state.minSessions?.toString() ?? '',
      maxSessions: state.maxSessions?.toString() ?? '',
    });
  }, [state, dirty]);

  const set = (key: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };
  const toCount = (v: string) => (v.trim() ? Number(v) : null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/projects/${projectId}/training/settings`, {
        audience: form.audience.trim() || null,
        language: form.language.trim() || null,
        minSessions: toCount(form.minSessions),
        maxSessions: toCount(form.maxSessions),
      });
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <form onSubmit={(e) => void save(e)}>
        <CardHeader>
          <CardTitle>Training profile</CardTitle>
          <CardDescription>
            {state.topic ? <span className="font-medium text-text">{state.topic}</span> : null}
            {state.complexity ? ` · ${state.complexity}` : ''}. Read from the sources — correct
            anything that's off.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="training-audience">Audience</Label>
              <Input
                id="training-audience"
                value={form.audience}
                maxLength={500}
                disabled={!canManage}
                onChange={(e) => set('audience', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="training-language">Language</Label>
              <Input
                id="training-language"
                value={form.language}
                maxLength={100}
                disabled={!canManage}
                onChange={(e) => set('language', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="training-min">Minimum sessions (optional)</Label>
              <Input
                id="training-min"
                type="number"
                min={1}
                max={40}
                value={form.minSessions}
                disabled={!canManage}
                placeholder={
                  state.suggestedMinSessions
                    ? `Suggested: ${state.suggestedMinSessions}`
                    : 'Let Gemini decide'
                }
                onChange={(e) => set('minSessions', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="training-max">Maximum sessions (optional)</Label>
              <Input
                id="training-max"
                type="number"
                min={1}
                max={40}
                value={form.maxSessions}
                disabled={!canManage}
                placeholder={
                  state.suggestedMaxSessions
                    ? `Suggested: ${state.suggestedMaxSessions}`
                    : 'Let Gemini decide'
                }
                onChange={(e) => set('maxSessions', e.target.value)}
              />
            </div>
          </div>
          {state.suggestedMinSessions && (
            <p className="text-xs text-muted">
              Gemini suggests{' '}
              {state.suggestedMinSessions === state.suggestedMaxSessions
                ? state.suggestedMinSessions
                : `${state.suggestedMinSessions}–${state.suggestedMaxSessions}`}{' '}
              sessions. {state.suggestionRationale}
            </p>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </CardContent>
        {canManage && (
          <CardFooter className="justify-end">
            <Button type="submit" variant="default" disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </CardFooter>
        )}
      </form>
    </Card>
  );
}

function QuestionsCard({
  projectId,
  state,
  canManage,
}: {
  projectId: string;
  state: TrainingState;
  canManage: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const answered = !!state.questionsAnsweredAt;
  const items = state.questions.map((q) => ({
    name: q.id,
    required: false,
    choices: q.suggestedOptions.map((opt) => ({ value: opt.label })),
  }));

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const answers: QuestionnaireAnswer[] = state.questions.map((q) => {
      const value = ((formData.get(q.id) as string | null) ?? '').trim();
      const isOption = q.suggestedOptions.some((o) => o.label === value);
      return {
        questionId: q.id,
        ...(value
          ? isOption
            ? { selectedOptionLabel: value }
            : { freeText: value.slice(0, 1000) }
          : {}),
      };
    });
    setBusy(true);
    setError(null);
    try {
      await api.put(`/projects/${projectId}/training/answers`, { answers });
      setEditing(false);
    } catch {
      setError('Could not save the answers.');
    } finally {
      setBusy(false);
    }
  }

  if (answered && !editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Follow-up questions</CardTitle>
          <CardDescription>Answered {relativeTime(state.questionsAnsweredAt)}.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {state.questions.map((q) => (
            <div key={q.id} className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{q.prompt}</span>
              <span className="text-sm text-muted">{answerText(state, q.id)}</span>
            </div>
          ))}
        </CardContent>
        {canManage && (
          <CardFooter className="justify-end">
            <Button onClick={() => setEditing(true)}>Change answers</Button>
          </CardFooter>
        )}
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-4">
        <h2 className="font-semibold">A few questions before planning the sessions</h2>
        <p className="text-sm text-muted">
          The sources left these open. Skip any you can't answer.
        </p>
      </div>
      {canManage ? (
        <Questionnaire items={items} onSubmit={(e) => void handleSubmit(e)} className="gap-6">
          <QuestionnaireProgress />
          {state.questions.map((q) => (
            <QuestionnaireItem key={q.id} name={q.id}>
              <QuestionnaireTitle>{q.prompt}</QuestionnaireTitle>
              {q.helpText && <QuestionnaireDescription>{q.helpText}</QuestionnaireDescription>}
              <QuestionnaireChoices>
                {q.suggestedOptions.map((opt) => (
                  <QuestionnaireChoice key={opt.label} value={opt.label}>
                    <span className="font-medium">{opt.label}</span>
                    {opt.description && (
                      <QuestionnaireChoiceDescription>
                        {opt.description}
                      </QuestionnaireChoiceDescription>
                    )}
                  </QuestionnaireChoice>
                ))}
                <QuestionnaireInput
                  placeholder={
                    q.suggestedOptions.length ? 'or type your own answer' : 'Your answer'
                  }
                />
              </QuestionnaireChoices>
              <QuestionnaireError />
            </QuestionnaireItem>
          ))}
          <QuestionnaireActions>
            {editing && (
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
            <QuestionnairePrevious />
            <QuestionnaireNext />
            <QuestionnaireSubmit disabled={busy}>
              {busy ? 'Saving…' : 'Save answers'}
            </QuestionnaireSubmit>
          </QuestionnaireActions>
        </Questionnaire>
      ) : (
        <p className="text-sm text-muted">Waiting for a project lead to answer them.</p>
      )}
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </Card>
  );
}

/**
 * Step one of a training project: upload the context material, have Gemini read it (topic,
 * audience, language, a suggested session range, follow-up questions), correct the profile and
 * answer the questions. Sources added once the sessions are confirmed flag the sessions they
 * affect (the training_impact job).
 */
export function TrainingSourcesPage() {
  const { projectId, role } = useProject();
  const canManage = canProject(role, 'training.manage');
  const { data: state } = useTrainingState(projectId);
  const { data: sources } = useTrainingSources(projectId);
  const { data: sessions } = useTrainingSessions(projectId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const analysis = useJobRunner(() => setNotice(null));
  const impact = useJobRunner((result) => {
    // No result: the files were added before any sessions existed, so there was nothing to check.
    if (!result) return;
    const affected = (result?.affected as unknown[] | undefined)?.length ?? 0;
    setNotice(
      affected
        ? `${affected} session${affected === 1 ? '' : 's'} may need updating — they're flagged on the Sessions tab.`
        : 'The new material doesn’t affect any existing session.',
    );
  });

  async function handleFiles(files: File[]) {
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    setNotice(null);
    try {
      const uploaded = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          storagePath: await uploadSourceFile(projectId, 'training/sources', file),
        })),
      );
      await impact.start(
        () => postJob(projectId, '/sources', { sources: uploaded }),
        'Could not add the files.',
      );
    } catch (err) {
      setUploadError(uploadErrorMessage(err, 'Could not upload the files.'));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeSource(id: string) {
    if (!window.confirm('Remove this source? Generations from now on won’t read it.')) return;
    await api
      .delete(`/projects/${projectId}/training/sources/${id}`)
      .catch(() => setUploadError('Could not remove the file.'));
  }

  const analyzed = !!state?.analyzedAt;
  const questionsPending = !!state && state.questions.length > 0 && !state.questionsAnsweredAt;
  const full = sources.length >= TRAINING_SOURCE_MAX_FILES;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 sm:p-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Sources</h1>
        <p className="mt-1 text-sm text-muted">
          Company information, documentation, answers to questions — everything the training should
          be built from.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Context files ({sources.length}/{TRAINING_SOURCE_MAX_FILES})
          </CardTitle>
          <CardDescription>
            PDF, Markdown, text, CSV or images. Word documents can be saved as PDF first.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {sources.length === 0 && <p className="text-sm text-muted">No files yet.</p>}
          {sources.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted" />
              <span className="min-w-0 flex-1 truncate">{s.filename}</span>
              <span className="shrink-0 text-xs text-muted">{relativeTime(s.addedAt)}</span>
              {canManage && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${s.filename}`}
                  className="h-10 w-10 shrink-0 md:h-8 md:w-8"
                  onClick={() => void removeSource(s.id)}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ))}
          {uploadError && <p className="text-sm text-danger">{uploadError}</p>}
          {impact.running && (
            <p className="text-sm text-muted">Checking which sessions the new material affects…</p>
          )}
          {impact.error && <p className="text-sm text-danger">{impact.error}</p>}
          {notice && <p className="text-sm text-muted">{notice}</p>}
        </CardContent>
        {canManage && (
          <CardFooter className="flex-wrap justify-end gap-2">
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={SOURCE_FILE_ACCEPT}
              onChange={(e) => void handleFiles(Array.from(e.target.files ?? []))}
            />
            <Button disabled={uploading || full} onClick={() => fileInput.current?.click()}>
              <Upload />
              {uploading ? 'Uploading…' : 'Add files'}
            </Button>
            <Button
              variant={analyzed ? 'secondary' : 'default'}
              disabled={sources.length === 0 || analysis.running || uploading}
              onClick={() =>
                void analysis.start(
                  () => postJob(projectId, '/analyze'),
                  'Could not start the analysis.',
                )
              }
            >
              <RefreshCw />
              {analysis.running
                ? 'Reading the sources…'
                : analyzed
                  ? 'Re-analyse sources'
                  : 'Analyse sources'}
            </Button>
          </CardFooter>
        )}
      </Card>
      {analysis.error && (
        <Card className="border-danger p-4 text-sm text-danger">{analysis.error}</Card>
      )}

      {state && analyzed && (
        <ProfileCard projectId={projectId} state={state} canManage={canManage} />
      )}
      {state && analyzed && state.questions.length > 0 && (
        <QuestionsCard projectId={projectId} state={state} canManage={canManage} />
      )}

      {analyzed && !questionsPending && (
        <Link
          to={`/projects/${projectId}/sessions`}
          className="self-end text-sm font-medium text-accent"
        >
          {sessions.length ? 'Go to sessions →' : 'Continue to planning the sessions →'}
        </Link>
      )}
    </div>
  );
}
